/**
 * Certificate Form Component
 * 
 * Form for creating and editing certificates
 * Handles PFX file upload and alias input
 * 
 * @component
 * @example
 * <CertificateForm
 *   mode="create"
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 * />
 */

import { FC, useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FiUpload, FiEye, FiEyeOff } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import { organizationService } from '@/services';
import type { Organization } from '@/services';
import type { CertificateFormProps } from './CertificateForm.types';
import styles from './CertificateForm.module.css';

/**
 * Validation Schema for Certificate Form
 */
const certificateSchema = z.object({
  alias: z
    .string()
    .min(1, 'Alias é obrigatório')
    .min(3, 'Alias deve ter pelo menos 3 caracteres')
    .max(255, 'Alias não pode ter mais de 255 caracteres'),
  password: z
    .string()
    .min(1, 'Senha é obrigatória')
    .min(6, 'Senha deve ter pelo menos 6 caracteres'),
  organizationId: z
    .number({
      required_error: 'Selecione uma organização',
      invalid_type_error: 'Selecione uma organização válida',
    })
    .int('Organização inválida')
    .positive('Selecione uma organização'),
  file: z
    .instanceof(File)
    .refine((file) => file.size > 0, 'Arquivo é obrigatório')
    .refine(
      (file) => file.type === 'application/x-pkcs12' || file.name.endsWith('.pfx'),
      'Apenas arquivos PFX são permitidos'
    )
    .optional(),
});

type CertificateFormSchemaType = z.infer<typeof certificateSchema>;

/**
 * CertificateForm Component
 */
const CertificateForm: FC<CertificateFormProps> = ({
  mode,
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Organization select states
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CertificateFormSchemaType>({
    resolver: zodResolver(certificateSchema),
    defaultValues: {
      alias: initialValues?.alias || '',
      password: initialValues?.password || '',
      organizationId: initialValues?.organizationId,
      file: initialValues?.file,
    },
  });

  const fileValue = watch('file');

  /**
   * Load organizations on mount
   */
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setIsLoadingOrgs(true);
        console.log('🔍 Iniciando carregamento de organizações...');
        
        // Buscar organizações da API
        const response = await organizationService.getAll(
          { page: 0, size: 100, sort: 'name,asc' }
        );
        
        console.log('📋 Response completa:', response);
        console.log('📋 Organizações carregadas:', response.data);
        console.log('📋 Total de organizações:', response.data?.length);
        
        setOrganizations(response.data || []);
      } catch (error) {
        console.error('❌ Erro ao carregar organizações:', error);
      } finally {
        setIsLoadingOrgs(false);
      }
    };

    loadOrganizations();
  }, []);

  /**
   * Reset form when initialValues change and organizations are loaded
   */
  useEffect(() => {
    if (initialValues && organizations.length > 0) {
      console.log('🔄 Resetando formulário com valores iniciais:', initialValues);
      reset({
        alias: initialValues.alias || '',
        password: initialValues.password || '',
        organizationId: initialValues.organizationId,
        file: initialValues.file,
      });
    }
  }, [initialValues, organizations, reset]);

  /**
   * Handle form submission
   */
  const onFormSubmit = async (data: CertificateFormSchemaType) => {
    try {
      await onSubmit({
        alias: data.alias,
        file: data.file,
        password: data.password,
        organizationId: data.organizationId
      });
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  /**
   * Handle file input change
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setValue('file', file);
    }
  };

  /**
   * Handle file input click
   */
  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onFormSubmit)}>
      {/* Alias Field */}
      <div className={styles.formGroup}>
      <label htmlFor="alias" className={styles.label}>
        Alias do Certificado
        <span className={styles.required}>*</span>
      </label>
      <input
        id="alias"
        type="text"
        placeholder="Ex: Production SSL Certificate"
        className={styles.input}
        aria-invalid={!!errors.alias}
        {...register('alias')}
        disabled={isLoading || isSubmitting}
      />
      {errors.alias && (
        <span className={styles.error}>{errors.alias.message}</span>
      )}
      <p className={styles.helpText}>
        Digite um nome descritivo para identificar este certificado
      </p>
      </div>

      {/* Password Field */}
        {/* Password Field */}
        <div className={styles.formGroup}>
        <label htmlFor="password" className={styles.label}>
          Senha do Certificado
          <span className={styles.required}>*</span>
        </label>
        <div className={styles.passwordWrapper}>
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Digite a senha do certificado PFX"
            className={styles.passwordInput}
            aria-invalid={!!errors.password}
            {...register('password')}
            disabled={isLoading || isSubmitting}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className={styles.togglePasswordButton}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            disabled={isLoading || isSubmitting}
          >
            {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
          </button>
        </div>
        {errors.password && (
          <span className={styles.error}>{errors.password.message}</span>
        )}
        <p className={styles.helpText}>
          Senha necessária para descriptografar o arquivo PFX
        </p>
      </div>

      {/* Organization Select */}
      <div className={styles.formGroup}>
        <label htmlFor="organizationId" className={styles.label}>
          Organização
          <span className={styles.required}>*</span>
        </label>
        <select
          id="organizationId"
          className={styles.select}
          aria-invalid={!!errors.organizationId}
          {...register('organizationId', { valueAsNumber: true })}
          disabled={isLoading || isSubmitting || isLoadingOrgs}
        >
          <option value="">
            {isLoadingOrgs ? 'Carregando organizações...' : 'Selecione uma organização'}
          </option>
          {organizations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </select>
        {errors.organizationId && (
          <span className={styles.error}>{errors.organizationId.message}</span>
        )}
        <p className={styles.helpText}>
          Selecione a organização à qual o certificado pertence
        </p>
        {!isLoadingOrgs && organizations.length === 0 && (
          <p className={styles.error}>
            Nenhuma organização ativa encontrada. Por favor, crie uma organização primeiro.
          </p>
        )}
      </div>

      {/* File Upload Field */}
      {mode === 'create' && (
      <div className={styles.formGroup}>
        <label htmlFor="file" className={styles.label}>
        Arquivo PFX
        <span className={styles.required}>*</span>
        </label>

        {/* Hidden File Input */}
        <input
        ref={fileInputRef}
        id="file"
        type="file"
        accept=".pfx,.p12"
        onChange={handleFileChange}
        className={styles.hiddenInput}
        />

        {/* File Upload Area */}
        <div
        className={`${styles.fileUploadArea} ${
          errors.file ? styles.fileUploadAreaError : ''
        }`}
        onClick={handleFileInputClick}
        >
        <FiUpload size={32} className={styles.uploadIcon} />
        {fileValue ? (
          <div className={styles.fileInfo}>
          <p className={styles.fileName}>{fileValue.name}</p>
          <p className={styles.fileSize}>
            {(fileValue.size / 1024).toFixed(2)} KB
          </p>
          </div>
        ) : (
          <div className={styles.uploadText}>
          <p className={styles.uploadTitle}>Clique para selecionar ou arraste o arquivo</p>
          <p className={styles.uploadSubtitle}>Apenas arquivos PFX (*.pfx, *.p12)</p>
          </div>
        )}
        </div>

        {errors.file && (
        <span className={styles.error}>{errors.file.message}</span>
        )}
        <p className={styles.helpText}>
        Selecione o arquivo PFX do seu certificado. O arquivo será validado antes do upload.
        </p>
      </div>
      )}

      {/* Edit Mode - File Info */}
      {mode === 'edit' && (
      <div className={styles.formGroup}>
        <label className={styles.label}>
        Arquivo de Certificado
        </label>
        <div className={styles.readOnlyInfo}>
        <p className={styles.readOnlyText}>
          Para alterar o certificado, crie um novo certificado ou contacte o administrador.
        </p>
        </div>
      </div>
      )}

      {/* Form Actions */}
      <div className={styles.actions}>
      <Button
        type="button"
        variant="secondary"
        onClick={onCancel}
        disabled={isLoading || isSubmitting}
      >
        Cancelar
      </Button>
      <Button
        type="submit"
        variant="primary"
        disabled={isLoading || isSubmitting}
      >
        {isLoading || isSubmitting ? 'Processando...' : 
        mode === 'create' ? 'Criar Certificado' : 'Atualizar Certificado'}
      </Button>
      </div>
    </form>
  );
};

export default CertificateForm;
